/**
 * Dragging a chat around the Workspace (into Pinned, within it to reorder,
 * out of it to unpin). Pointer events, not HTML5 drag-and-drop: the drag
 * starts after a few pixels so clicks still open chats, a ghost follows the
 * cursor, and the drop target is read from what is under the pointer:
 *   [data-ws-drop="pinned"]  the Pinned widget
 *   [data-chat-key]          a card inside it (dropping inserts before it)
 */
import { useCallback, useEffect, useRef, useState, type PointerEvent as ReactPointerEvent } from 'react';

export interface DragChat {
  account_id: string;
  fan_id: string;
  fan_name: string | null;
  fan_username: string | null;
  fan_avatar: string | null;
}

/** Where the drag started: a Pinned card can be dragged out to unpin it. */
export type DragSource = 'pinned' | 'other';

export interface DropTarget {
  pinned: boolean;
  /** Card key (`p:…`) the chat was dropped on, inside Pinned. */
  beforeKey: string | null;
}

export interface ChatDragState {
  chat: DragChat;
  source: DragSource;
  /** Key of the card being dragged (dimmed in its list). */
  key: string | null;
  x: number;
  y: number;
  over: DropTarget;
}

const THRESHOLD = 6;

export function dropTargetAt(x: number, y: number): DropTarget {
  const el = document.elementFromPoint(x, y) as HTMLElement | null;
  const zone = el?.closest<HTMLElement>('[data-ws-drop="pinned"]');
  if (!zone) return { pinned: false, beforeKey: null };
  const card = el?.closest<HTMLElement>('[data-chat-key]');
  return { pinned: true, beforeKey: card && zone.contains(card) ? card.dataset.chatKey ?? null : null };
}

export function useChatDrag(onDrop: (chat: DragChat, source: DragSource, target: DropTarget) => void, enabled = true) {
  const [drag, setDrag] = useState<ChatDragState | null>(null);
  const onDropRef = useRef(onDrop);
  onDropRef.current = onDrop;
  const stop = useRef<(() => void) | null>(null);
  useEffect(() => () => stop.current?.(), []);

  const start = useCallback(
    (chat: DragChat, source: DragSource, e: ReactPointerEvent, key: string | null = null) => {
      // Mouse/pen only: on touch the same gesture scrolls the list.
      if (!enabled || e.button !== 0 || e.pointerType === 'touch') return;
      if ((e.target as HTMLElement).closest('.ws-card-act, .chat-tab-action, input, textarea, a')) return;
      stop.current?.();
      const sx = e.clientX;
      const sy = e.clientY;
      let active = false;

      const move = (ev: PointerEvent) => {
        if (!active) {
          if (Math.hypot(ev.clientX - sx, ev.clientY - sy) < THRESHOLD) return;
          active = true;
          window.getSelection()?.removeAllRanges();
        }
        ev.preventDefault();
        setDrag({ chat, source, key, x: ev.clientX, y: ev.clientY, over: dropTargetAt(ev.clientX, ev.clientY) });
      };
      const up = (ev: PointerEvent) => {
        const wasActive = active;
        cleanup();
        if (!wasActive) return;
        // The click that ends a drag must not open the chat under the pointer.
        const swallow = (c: MouseEvent) => {
          c.stopPropagation();
          c.preventDefault();
        };
        window.addEventListener('click', swallow, true);
        window.setTimeout(() => window.removeEventListener('click', swallow, true), 0);
        onDropRef.current(chat, source, dropTargetAt(ev.clientX, ev.clientY));
      };
      const onKey = (ev: KeyboardEvent) => {
        if (ev.key === 'Escape' && active) cleanup();
      };
      const cleanup = () => {
        window.removeEventListener('pointermove', move);
        window.removeEventListener('pointerup', up);
        window.removeEventListener('pointercancel', cleanup);
        window.removeEventListener('keydown', onKey, true);
        stop.current = null;
        active = false;
        setDrag(null);
      };
      window.addEventListener('pointermove', move);
      window.addEventListener('pointerup', up);
      window.addEventListener('pointercancel', cleanup);
      window.addEventListener('keydown', onKey, true);
      stop.current = cleanup;
    },
    [enabled],
  );

  return { drag, start };
}
